import { Style, StylesGroup } from '@core/interfaces/styles/styles.interface';
import { StyleGroupBuilder } from '@core/services/common/styles/style-group-builder';
import { hasParallax, isTextGradientBackground } from '@core/services/common/styles/styles-helpers';

const hasOwnProperty = (thisArg: any, ...argArray: string[]) => Object.prototype.hasOwnProperty.call(thisArg, ...argArray);

const BG_SIZE_ANIMATIONS = ['bgSizeUp', 'bgSizeDown'];

// Właściwości tła przenoszone na ::before, gdy blok ma animację bgSizeUp/bgSizeDown.
// background-size jest tu celowo NIEZMIENIANE — to jest dokładnie to ustawienie ("cover"/
// "contain"/"auto" z modala "Wypełnij"/"Dopasuj"), które dotychczas był nadpisywane.
const BACKGROUND_PROPS = [
  'backgroundImage',
  'backgroundPosition',
  'backgroundPositionX',
  'backgroundPositionY',
  'backgroundRepeat',
  'backgroundColor',
  'backgroundSize',
  'backgroundAttachment',
  'backgroundClip',
  '-webkitBackgroundClip',
  'backgroundOrigin',
];

// Zmienna, przez którą blok podaje pseudoelementowi adres obrazu tła. Musi zostać na samym
// bloku, bo StyleService podmienia adres na wariant o szerokości bloku, a stylu inline nie
// da się zapisać na pseudoelemencie.
export const PSEUDO_BACKGROUND_IMAGE_VAR = '--block-background-image';

export class StyleBuilder {
  static build(blockId: string, styleGroups: StylesGroup): string {
    let mergeGroupString = '';
    const extraction = StyleBuilder.extractBackgroundToPseudoElementForSizeAnimation(styleGroups, blockId);
    styleGroups = StyleBuilder.applyFadeInTransition(extraction.styleGroups);
    styleGroups = StyleBuilder.correctPositionProperty(styleGroups);

    for (const group of Object.getOwnPropertyNames(styleGroups)) {
      const str = StyleGroupBuilder.build(blockId, group, styleGroups[group]);
      mergeGroupString = `${mergeGroupString} ${str}`;
    }
    return `{${mergeGroupString} ${extraction.pseudoElementCss}}`;
  }

  /**
   * background-size nie da się animować pomiędzy "cover" a wartością procentową (przeglądarki
   * tego nie interpolują) - stąd zamiast animować background-size na bloku (co nadpisywało
   * "cover" sztywnym procentem), przenosimy malowanie tła na ::before i animujemy tam
   * samodzielną właściwość `scale` (przyjmuje wartości procentowe, więc reużywa istniejących
   * zmiennych --bgSizeUpBgSizeStart/End 1:1). background-size na ::before zostaje nietknięte.
   *
   * Animacja tła bywa zapisana w dwóch stanach i oba trafiają tutaj: w `hover` (uruchamia ją
   * najechanie kursorem) oraz w `basic` (uruchamia ją wejście bloku w widok). Animacja musi
   * siedzieć wyłącznie na pseudoelemencie — zostawiona na bloku skalowałaby też jego treść.
   */
  private static extractBackgroundToPseudoElementForSizeAnimation(
    styleGroups: StylesGroup,
    blockId: string,
  ): { styleGroups: StylesGroup; pseudoElementCss: string } {
    if (!hasOwnProperty(styleGroups, 'basic')) {
      return { styleGroups, pseudoElementCss: '' };
    }

    // Tło strony jest malowane wprost na pseudoelemencie (`app-root:after`), a do pseudoelementu
    // nie da się dołożyć kolejnego — `app-root:after::before` to selektor niepoprawny i przeglądarka
    // odrzuca całą regułę razem z tłem. Taki cel zostaje przy malowaniu tła na sobie samym.
    if (blockId.includes(':')) {
      return { styleGroups, pseudoElementCss: '' };
    }

    const { basic } = styleGroups;
    const hover = hasOwnProperty(styleGroups, 'hover') ? styleGroups.hover : null;
    const isHoverAnimation = BG_SIZE_ANIMATIONS.includes(hover?.animationName);
    const animationSource = isHoverAnimation
      ? hover
      : (BG_SIZE_ANIMATIONS.includes(basic.animationName) ? basic : null);

    if (!animationSource) {
      return { styleGroups, pseudoElementCss: '' };
    }

    // Animacja tła bez samego tła nie ma czego powiększać. Zdarza się, gdy właściwości tła
    // i animacji rozjeżdżają się na osobne reguły (tak buduje się styl strony) — wtedy sam
    // pseudoelement byłby pusty, a blok niepotrzebnie dostałby position i overflow.
    if (!BACKGROUND_PROPS.some(prop => hasOwnProperty(basic, prop))) {
      return { styleGroups, pseudoElementCss: '' };
    }

    if (isTextGradientBackground(basic) || hasParallax(basic)) {
      // Tekst-gradient: dzieci dziedziczą background-* przez CSS `inherit` bezpośrednio
      // z bloku (block.component.scss) - `inherit` nie sięga do ::before.
      // Parallax: main-page.component.ts ustawia el.style.backgroundPositionY wprost na
      // bloku - na ::before ten zapis stałby się no-opem.
      // W obu przypadkach zachowujemy dotychczasowe (nadal błędne dla "cover") zachowanie
      // zamiast ciszej psuć inny, niezależny mechanizm.
      return {
        styleGroups: isHoverAnimation ? StyleBuilder.applyLegacyBgSizeAnimation(styleGroups) : styleGroups,
        pseudoElementCss: '',
      };
    }

    const { animationName, animationDuration, animationIterationCount, animationDelay } = animationSource;
    const isInfinite = animationIterationCount === 'infinite';

    const pseudoBasic: Style = {
      content: '\'\'',
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      zIndex: '-1',
      pointerEvents: 'none',
    };
    for (const prop of BACKGROUND_PROPS) {
      if (hasOwnProperty(basic, prop)) {
        pseudoBasic[prop] = basic[prop];
        delete basic[prop];
      }
    }
    if (pseudoBasic.backgroundImage) {
      basic[PSEUDO_BACKGROUND_IMAGE_VAR] = pseudoBasic.backgroundImage;
      pseudoBasic.backgroundImage = `var(${PSEUDO_BACKGROUND_IMAGE_VAR})`;
    }

    if (!basic.position || basic.position === 'static') {
      basic.position = 'relative';
    }
    // Pseudoelement ma z-index:-1, więc maluje się pod treścią bloku. Bez własnego kontekstu
    // stosu na bloku trafia jednak do kontekstu przodka i chowa się także pod tłem sekcji
    // nadrzędnej — obrazek tła znika. Samo `position` tego nie załatwia: kontekst powstaje
    // dopiero przy z-indeksie innym niż `auto`, a tu go nie ustawiamy (i nie chcemy, bo
    // wpłynąłby na kolejność malowania bloku wobec rodzeństwa). `isolation` tworzy kontekst
    // bez dotykania z-indeksu. Ustawiane bezwarunkowo, bo klient mógł mieć własne
    // `position: absolute` i wtedy gałąź wyżej się nie wykona, a problem zostaje ten sam.
    basic.isolation = 'isolate';
    basic.overflow = 'hidden';
    // StyleGroupBuilder zamienia min-height w sztywną wysokość, gdy blok ma overflow:hidden.
    // Tutaj overflow jest wyłącznie przycięciem powiększonego tła i nie może zmieniać
    // wysokości bloku, więc z góry deklarujemy wysokość automatyczną.
    if (hasOwnProperty(basic, 'minHeight') && !hasOwnProperty(basic, 'height')) {
      basic.height = 'auto';
    }

    pseudoBasic.scale = `var(--${animationName}BgSizeStart)`;
    const infiniteDuration = `${+`${animationDuration}`.replace('s', '') * 2}s`;

    let pseudoHover: Style = {};
    if (isHoverAnimation) {
      if (isInfinite) {
        pseudoHover = {
          animationName: `${animationName}Infinite`,
          animationDuration: infiniteDuration,
        };
      } else {
        pseudoBasic.transitionProperty = 'scale !important';
        pseudoBasic.transitionDuration = `${animationDuration} !important`;
        pseudoBasic.transitionDelay = animationDelay || '0s';
        pseudoHover = { scale: `var(--${animationName}BgSizeEnd) !important` };
      }
      delete hover.animationName;
      delete hover.animationDuration;
      delete hover.animationIterationCount;
      delete hover.animationDelay;
    } else {
      pseudoBasic.animationName = isInfinite ? `${animationName}Infinite` : animationName;
      pseudoBasic.animationDuration = isInfinite ? infiniteDuration : animationDuration;
      pseudoBasic.animationIterationCount = animationIterationCount;
      if (animationDelay) {
        pseudoBasic.animationDelay = animationDelay;
      }
      if (!isInfinite) {
        // Powiększenie/pomniejszenie ma zostać na wartości docelowej po zakończeniu animacji.
        pseudoBasic.animationFillMode = 'forwards';
      }
      // Nazwa animacji musi zniknąć z bloku, ale zostaje w zmiennej, bo BlockComponent czyta
      // z niej atrybut animation-name — na nim opiera się odpalanie animacji po wejściu
      // bloku w widok (klasa visibleOnViewport).
      basic['--animation-name'] = animationName;
      delete basic.animationName;
      delete basic.animationDuration;
      delete basic.animationIterationCount;
      delete basic.animationDelay;
    }

    const pseudoElementCss =
      `${blockId}::before{${StyleGroupBuilder.toDeclarationString(pseudoBasic)}}` +
      (Object.keys(pseudoHover).length
        ? `${blockId}:hover::before{${StyleGroupBuilder.toDeclarationString(pseudoHover)}}`
        : '');

    return { styleGroups, pseudoElementCss };
  }

  /** Dotychczasowe zachowanie (przed AT-9059) - zachowane tylko dla textGradient/parallax, patrz wyżej. */
  private static applyLegacyBgSizeAnimation(styleGroups: StylesGroup): StylesGroup {
    const { basic, hover } = styleGroups;
    const { animationName, animationDuration, animationIterationCount, animationDelay } = hover;
    if (animationIterationCount === 'infinite') {
      basic.backgroundSize = `var(--${animationName}BgSizeStart)`;
      // Klatki w wariancie "Legacy": animacja zostaje na bloku, a klatki bgSize*Infinite
      // ruszają od AT-9059 właściwość `scale`, więc na bloku powiększyłyby całą jego treść.
      // Wariant legacy animuje rozmiar tła, czyli to, co te klatki robiły wcześniej.
      hover.animationName = animationName + 'LegacyInfinite';
      const newDuration = animationDuration.replace('s', '');
      hover.animationDuration = `${+newDuration * 2}s`;
    } else {
      delete hover.animationName;
      const transitionDelay = animationDelay || '0s';
      basic.transitionProperty = 'background-size !important';
      basic.transitionDuration = `${animationDuration} !important`;
      basic.transitionDelay = transitionDelay;
      basic.backgroundSize = `var(--${animationName}BgSizeStart)`;
      hover.backgroundSize = `var(--${animationName}BgSizeEnd) !important`;
    }
    return styleGroups;
  }

  private static applyFadeInTransition(styleGroups: StylesGroup): StylesGroup {
    if (hasOwnProperty(styleGroups, 'basic')) {
      const { basic } = styleGroups;
      if (['fadeIn', 'fadeInUp', 'fadeInDown', 'fadeInLeft', 'fadeInRight'].includes(basic.animationName)) {
        const transitionDuration = (basic.animationDuration || '1s');
        const transitionDelay = (basic.animationDelay || '0s');
        styleGroups.basic['--transition-duration'] = `${transitionDuration} !important`;
        styleGroups.basic['--transition-delay'] = transitionDelay;
        styleGroups.basic['--animation-name'] = basic.animationName;
        delete styleGroups.basic.animationName;
      }
    }
    return styleGroups;
  }

  private static correctPositionProperty(styleGroups: StylesGroup) {
    const correct = (v: Style) => {
      if (v.left === '50%' && v.top === '50%') {
        v.translate = '-50% -50%';
        return v;
      }
      if (v.left === '50%') {
        v.translate = '-50%';
        return v;
      }
      if (v.top === '50%') {
        v.translate = '0 -50%';
        return v;
      }

      return v;
    };

    Object.keys(styleGroups).forEach(key => {
      if (styleGroups[key]?.position === 'fixed') {
        styleGroups[key] = correct(styleGroups[key]);
      }
    });
    return styleGroups;
  }
}
